import {
  type FormEvent,
  type ReactNode,
  useId,
  useLayoutEffect,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../i18n/I18n";

/**
 * A modal `ds-dialog`, open for as long as it is mounted.
 *
 * It is a native `<dialog>` opened with `showModal()`, which gives the
 * backdrop, the focus trap, Escape to cancel and focus restoration for free —
 * everything `window.prompt` gave, without depending on the browser letting
 * the page show one. It is portalled to `<body>` so the card or rows it is
 * opened from cannot restyle it: SDS greys out primary buttons inside a
 * `ds-card` and turns any form under `ds-rows` into a grid.
 *
 * The body sits in a form, so Enter in a field submits it. The element marked
 * `data-autofocus` takes focus on open; otherwise the browser picks the first
 * focusable one.
 */
export function Dialog({
  title,
  description,
  tone,
  onSubmit,
  onCancel,
  submitLabel,
  submitDisabled = false,
  children,
}: {
  title: string;
  description?: string;
  /** `alert` for a destructive confirmation. */
  tone?: "alert";
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
  submitDisabled?: boolean;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  // Layout effect: its cleanup runs before React detaches the element, so
  // `close()` still restores focus to whatever opened the dialog.
  useLayoutEffect(() => {
    const dialog = ref.current;
    if (dialog === null) {
      return;
    }
    dialog.showModal();
    dialog.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    return () => dialog.close();
  }, []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!submitDisabled) {
      onSubmit();
    }
  };

  return createPortal(
    <dialog
      ref={ref}
      className={`ds-dialog --narrow${tone === "alert" ? " --alert" : ""}`}
      aria-labelledby={titleId}
      aria-describedby={description === undefined ? undefined : descriptionId}
      // Escape: let React unmount it rather than the browser closing it under us.
      onCancel={(event) => {
        event.preventDefault();
        onCancel();
      }}
    >
      <form onSubmit={submit}>
        <header className="ds-banner">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description !== undefined && (
              <p id={descriptionId}>{description}</p>
            )}
          </div>
        </header>
        {children !== undefined && <section>{children}</section>}
        <footer>
          <button
            type="submit"
            className={`ds-button ${tone === "alert" ? "--alert" : "--primary"}`}
            disabled={submitDisabled}
          >
            <span>{submitLabel}</span>
          </button>
          {/* A destructive dialog opens on Cancel, so a stray Enter is safe. */}
          <button
            type="button"
            className="ds-button"
            onClick={onCancel}
            data-autofocus={tone === "alert" ? true : undefined}
          >
            <span>{t("common.cancel")}</span>
          </button>
        </footer>
      </form>
    </dialog>,
    document.body,
  );
}

/** A yes/no question before something that cannot be undone. */
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  description?: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      title={title}
      {...(description === undefined ? {} : { description })}
      tone="alert"
      submitLabel={confirmLabel}
      onSubmit={onConfirm}
      onCancel={onCancel}
    />
  );
}
